import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Theme = "light" | "dark";

export type AccentColorId =
  | "orange"
  | "violet"
  | "blue"
  | "emerald"
  | "rose"
  | "amber"
  | "cyan"
  | "indigo";

export type AccentColor = {
  id: AccentColorId;
  label: string;
  /** HSL without hsl() wrapper — matches CSS vars, e.g. "24 95% 53%" */
  hsl: string;
  /** Preview swatch */
  hex: string;
};

export const ACCENT_COLORS: AccentColor[] = [
  { id: "orange", label: "Brand Red", hsl: "359 75% 49%", hex: "#D91F20" },
  { id: "violet", label: "Violet", hsl: "262 83% 58%", hex: "#7C3AED" },
  { id: "blue", label: "Blue", hsl: "217 91% 60%", hex: "#3B82F6" },
  { id: "emerald", label: "Emerald", hsl: "160 84% 39%", hex: "#10B981" },
  { id: "rose", label: "Rose", hsl: "347 77% 50%", hex: "#E11D48" },
  { id: "amber", label: "Amber", hsl: "38 92% 50%", hex: "#F59E0B" },
  { id: "cyan", label: "Cyan", hsl: "189 94% 43%", hex: "#06B6D4" },
  { id: "indigo", label: "Indigo", hsl: "239 84% 67%", hex: "#6366F1" },
];

const THEME_STORAGE_KEY = "bk-pos-theme";
const ACCENT_STORAGE_KEY = "bk-pos-accent";
const DEFAULT_ACCENT: AccentColorId = "orange";

function isAccentId(value: string | null): value is AccentColorId {
  return ACCENT_COLORS.some((c) => c.id === value);
}

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
}

export function getStoredAccentId(): AccentColorId {
  if (typeof window === "undefined") return DEFAULT_ACCENT;
  const raw = localStorage.getItem(ACCENT_STORAGE_KEY);
  return isAccentId(raw) ? raw : DEFAULT_ACCENT;
}

export function getAccentById(id: AccentColorId): AccentColor {
  return ACCENT_COLORS.find((c) => c.id === id) ?? ACCENT_COLORS[0];
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

/** Sets primary / ring / sidebar-primary / chart-1 so the whole UI follows. */
export function applyAccentColor(accentId: AccentColorId) {
  const accent = getAccentById(accentId);
  const root = document.documentElement;
  root.style.setProperty("--primary", accent.hsl);
  root.style.setProperty("--ring", accent.hsl);
  root.style.setProperty("--sidebar-primary", accent.hsl);
  root.style.setProperty("--sidebar-ring", accent.hsl);
  root.style.setProperty("--chart-1", accent.hsl);
  root.style.setProperty("--primary-border", `hsl(${accent.hsl})`);
  root.style.setProperty("--sidebar-primary-border", `hsl(${accent.hsl})`);
  root.dataset.accent = accent.id;
}

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  accentId: AccentColorId;
  accent: AccentColor;
  setAccentId: (id: AccentColorId) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [accentId, setAccentIdState] = useState<AccentColorId>(getStoredAccentId);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    applyAccentColor(accentId);
    localStorage.setItem(ACCENT_STORAGE_KEY, accentId);
  }, [accentId]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === "light" ? "dark" : "light"));
  }, []);

  const setAccentId = useCallback((id: AccentColorId) => {
    setAccentIdState(id);
  }, []);

  const accent = useMemo(() => getAccentById(accentId), [accentId]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
      accentId,
      accent,
      setAccentId,
    }),
    [theme, setTheme, toggleTheme, accentId, accent, setAccentId],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
